const STATUS_FALLBACKS = {
  400: 'The request was rejected. Please check the details and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to do that.',
  404: 'That item could not be found.',
  408: 'The request timed out. Please try again.',
  409: 'That conflicts with something that already exists.',
  413: 'That file is too large to upload.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Something went wrong on the server. Please try again.',
  502: 'The server is unreachable right now. Please try again.',
  503: 'The service is temporarily unavailable. Please try again.',
  504: 'The server took too long to respond. Please try again.',
};

const firstNonEmpty = (...values) =>
  values.find((v) => typeof v === 'string' && v.trim().length > 0)?.trim() ?? null;

export function extractApiMessage(payload) {
  if (payload == null) return null;

  if (typeof payload === 'string') {
    const text = payload.trim();
    if (!text || text.startsWith('<')) return null;
    if (text.startsWith('{') || text.startsWith('[') || text.startsWith('"')) {
      try {
        return extractApiMessage(JSON.parse(text));
      } catch {
        return text;
      }
    }
    return text;
  }

  if (Array.isArray(payload)) {
    for (const entry of payload) {
      const found = extractApiMessage(entry);
      if (found) return found;
    }
    return null;
  }

  if (typeof payload !== 'object') return null;

  const direct = firstNonEmpty(
    payload.Message,
    payload.message,
    payload.Result?.Message,
    payload.Result?.message,
    payload.error_description,
    payload.detail,
    payload.Detail
  );
  if (direct) return direct;

  const errors = payload.errors ?? payload.Errors;
  if (errors && typeof errors === 'object') {
    const flattened = Object.values(errors).flat();
    const first = firstNonEmpty(...flattened);
    if (first) return first;
  }

  return firstNonEmpty(payload.title, payload.Title, payload.error);
}

export function statusFallbackMessage(status) {
  if (!status) return 'Could not reach the server. Check your connection and try again.';
  return STATUS_FALLBACKS[status] ?? `The request failed (${status}). Please try again.`;
}

export function describeAxiosError(error) {
  if (!error) return statusFallbackMessage(null);

  const response = error.response;
  if (!response) {
    if (error.code === 'ECONNABORTED') return 'The request timed out. Please try again.';
    return statusFallbackMessage(null);
  }

  return extractApiMessage(response.data) ?? statusFallbackMessage(response.status);
}
